/**
 * Account Store - Zustand State Management
 * 
 * Manages bank account state.
 * 
 * @validates Requirements 4.1 - Account management
 */

import { create } from 'zustand';
import { BankAccount, CreateBankAccountRequest, UpdateBankAccountRequest } from '@budgira/shared';
import { accountService, AccountBalance } from '../services/account.service';
import { localizeError } from '../utils/errorMapper';
import { getDefaultAccountId } from './userSettingsStore';

interface AccountState {
  accounts: BankAccount[];
  selectedAccount: BankAccount | null;
  balances: Record<string, AccountBalance>;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchAccounts: () => Promise<void>;
  selectAccount: (accountId: string | null) => void;
  selectDefaultAccount: () => void;
  createAccount: (data: CreateBankAccountRequest) => Promise<BankAccount | null>;
  updateAccount: (id: string, data: UpdateBankAccountRequest) => Promise<BankAccount | null>;
  deleteAccount: (id: string) => Promise<boolean>;
  fetchBalance: (accountId: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export const useAccountStore = create<AccountState>((set, get) => ({
  accounts: [],
  selectedAccount: null,
  balances: {},
  isLoading: false,
  error: null,

  fetchAccounts: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const response = await accountService.list();
      if (response.success && response.data) {
        const accounts = response.data;
        const { selectedAccount } = get();
        
        // Update selectedAccount with fresh data (including recalculated balance)
        let updatedSelectedAccount = selectedAccount;
        if (selectedAccount) {
          updatedSelectedAccount = accounts.find((a) => a.id === selectedAccount.id) || null;
        } else if (accounts.length > 0) {
          // Check if user has a default account preference
          const defaultAccountId = getDefaultAccountId();
          if (defaultAccountId) {
            updatedSelectedAccount = accounts.find((a) => a.id === defaultAccountId) || accounts[0] || null;
          } else {
            // Auto-select first account if none selected and no preference
            updatedSelectedAccount = accounts[0] || null;
          }
        }
        
        set({ accounts, selectedAccount: updatedSelectedAccount, isLoading: false });
        
        // Fetch balances for all accounts
        accounts.forEach((account) => {
          get().fetchBalance(account.id);
        });
      } else {
        set({ error: response.error?.message || 'Failed to fetch accounts', isLoading: false });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
    }
  },

  selectAccount: (accountId: string | null): void => {
    const { accounts } = get();
    const account = accountId ? accounts.find((a) => a.id === accountId) || null : null;
    set({ selectedAccount: account });
  },

  selectDefaultAccount: (): void => {
    const { accounts } = get();
    if (accounts.length === 0) return;
    
    const defaultAccountId = getDefaultAccountId();
    if (defaultAccountId) {
      const defaultAccount = accounts.find((a) => a.id === defaultAccountId);
      if (defaultAccount) {
        set({ selectedAccount: defaultAccount });
        return;
      }
    }
    // Fallback to first account
    set({ selectedAccount: accounts[0] || null });
  },

  createAccount: async (data: CreateBankAccountRequest): Promise<BankAccount | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await accountService.create(data);
      if (response.success && response.data) {
        const newAccount = response.data;
        set((state) => ({
          accounts: [...state.accounts, newAccount],
          isLoading: false,
        }));
        return newAccount;
      } else {
        set({ error: response.error?.message || 'Failed to create account', isLoading: false });
        return null;
      }
    } catch (err: unknown) {
      const message = localizeError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  updateAccount: async (id: string, data: UpdateBankAccountRequest): Promise<BankAccount | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await accountService.update(id, data);
      if (response.success && response.data) {
        const updatedAccount = response.data;
        set((state) => ({
          accounts: state.accounts.map((a) => (a.id === id ? updatedAccount : a)),
          selectedAccount: state.selectedAccount?.id === id ? updatedAccount : state.selectedAccount,
          isLoading: false,
        }));
        return updatedAccount;
      } else {
        set({ error: response.error?.message || 'Failed to update account', isLoading: false });
        return null;
      }
    } catch (err: unknown) {
      const message = localizeError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  deleteAccount: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      const response = await accountService.delete(id);
      if (response.success) {
        set((state) => ({
          accounts: state.accounts.filter((a) => a.id !== id),
          selectedAccount: state.selectedAccount?.id === id ? null : state.selectedAccount,
          isLoading: false,
        }));
        return true;
      } else {
        set({ error: response.error?.message || 'Failed to delete account', isLoading: false });
        return false;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
      return false;
    }
  },

  fetchBalance: async (accountId: string): Promise<void> => {
    try {
      const response = await accountService.getBalance(accountId);
      if (response.success && response.data) {
        set((state) => ({
          balances: { ...state.balances, [accountId]: response.data as AccountBalance },
        }));
      }
    } catch {
      // Silently fail for balance fetching
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set({
    accounts: [],
    selectedAccount: null,
    balances: {},
    isLoading: false,
    error: null,
  }),
}));

export default useAccountStore;
